/**
 * The Payment methods screen's URL view state and sort columns (DNKADM-0430 P1-F).
 *
 * URL params on `/settings/payments/payment-methods` (all optional; a default is never
 * written):
 * - `q`: search on the method name.
 * - `sort` + `dir`: the column sort. The default is today's fixed order: the display order
 *   (`sortOrder`, then name), shown as the Order column ascending.
 *
 * A column sort is a VIEW only: it never writes `sortOrder`. Moving a method up or down
 * (the only thing that changes `sortOrder`) is offered only in the default order with no
 * search, where the rows on screen are the display order.
 */
import type { PaymentMethod, PaymentMethodKind } from '@/api/paymentMethods';
import { matchesSearch } from '@/components/list/listSearch';
import type { SortState, SortValue } from '@/components/list/listSort';

export const PAYMENT_METHODS_URL_DEFAULTS = { q: '', sort: '', dir: '' };

export type PaymentMethodSortKey = 'order' | 'name' | 'type' | 'status';
export const PAYMENT_METHOD_SORT_KEYS: readonly PaymentMethodSortKey[] = [
  'order',
  'name',
  'type',
  'status',
];
export const PAYMENT_METHOD_DEFAULT_SORT: SortState<PaymentMethodSortKey> = {
  key: 'order',
  dir: 'asc',
};

/** Human label for a method kind chip. */
export const KIND_LABEL: Record<PaymentMethodKind, string> = {
  cash: 'Cash',
  card: 'Card',
  external: 'Custom',
};

/** Display order: by `sortOrder`, then label (stable, case-insensitive). */
export function bySortOrder(a: PaymentMethod, b: PaymentMethod): number {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  return a.label.localeCompare(b.label);
}

/**
 * What a method row shows (and sorts by) in each column. `position` is the row's
 * 1-based place in the display order (the Order column).
 */
export function paymentMethodSortValue(
  method: PaymentMethod,
  key: PaymentMethodSortKey,
  position: number,
): SortValue {
  switch (key) {
    case 'order':
      return position;
    case 'name':
      return method.label;
    case 'type':
      return KIND_LABEL[method.kind];
    case 'status':
      return method.active ? 'Active' : 'Inactive';
  }
}

/** The Payment methods search: the method name. */
export function paymentMethodMatches(method: PaymentMethod, query: string): boolean {
  return matchesSearch(query, [method.label]);
}
